import { useAuth, useSignUp } from "@clerk/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AuthScreen } from "@/components/auth/auth-screen";
import { CodeInput } from "@/components/auth/code-input";
import { PrimaryButton } from "@/components/auth/primary-button";
import { ResendRow } from "@/components/auth/resend-row";
import { TextButton } from "@/components/auth/text-link";
import { authPath } from "@/lib/auth-helpers";
import { clerkFailureMessage } from "@/lib/clerk-errors";
import { codeDigits, emptyCode } from "@/lib/code-input";
import { clearCodeSent } from "@/lib/code-sent";
import { followDecorated } from "@/lib/follow-auth";
import { useSendCodeOnce } from "@/lib/use-send-code-once";

const FLOW = "sign-up";

const SignUpVerifyPage = () => {
  const navigate = useNavigate();
  const { isLoaded } = useAuth();
  const { fetchStatus, signUp } = useSignUp();
  const [code, setCode] = useState(emptyCode);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const email = signUp.emailAddress ?? "";
  const ready =
    Boolean(email) && signUp.unverifiedFields.includes("email_address");

  useEffect(() => {
    if (isLoaded && !ready) {
      navigate(authPath("/signup"), { replace: true });
    }
  }, [isLoaded, navigate, ready]);

  const send = useCallback(
    () => signUp.verifications.sendEmailCode(),
    [signUp]
  );
  useSendCodeOnce(FLOW, ready, send);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const digits = codeDigits(code);
    if (digits.length !== 6) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setError(null);
    setPending(true);
    const { error: verifyError } = await signUp.verifications.verifyEmailCode({
      code: digits,
    });
    if (verifyError) {
      setError(clerkFailureMessage(verifyError));
      setPending(false);
      return;
    }
    if (signUp.status !== "complete") {
      setError("Something went wrong. Try again.");
      setPending(false);
      return;
    }
    const { error: finalError } = await signUp.finalize({
      navigate: ({ decorateUrl, session }) => {
        if (session.currentTask?.key === "reset-password") {
          followDecorated(
            decorateUrl,
            authPath("/session-tasks/reset-password")
          );
          return;
        }
        followDecorated(decorateUrl, authPath("/signup/organization"));
      },
    });
    if (finalError) {
      setError(clerkFailureMessage(finalError));
      setPending(false);
    }
  };

  return (
    <AuthScreen
      eyebrow="Step 2 of 3 · Verify email"
      subtitle={
        <>
          We sent a 6-digit code to{" "}
          <span className="font-medium text-navy-900">{email}</span>. Enter it
          below to verify your email.
        </>
      }
      title="Check your email"
    >
      <form className="flex flex-col gap-8" onSubmit={onSubmit}>
        <CodeInput
          error={error}
          id="signup-code"
          label="Verification code"
          onChange={(value) => {
            setCode(value);
            setError(null);
          }}
          value={code}
        />
        <PrimaryButton
          busy={pending || fetchStatus === "fetching"}
          type="submit"
        >
          Verify email
        </PrimaryButton>
        <ResendRow
          alternate={
            <TextButton
              onClick={() => {
                signUp.reset().finally(() => {
                  navigate(authPath("/signup"));
                });
              }}
            >
              Use a different email
            </TextButton>
          }
          flow={FLOW}
          onResend={async () => {
            const { error: resendError } =
              await signUp.verifications.sendEmailCode();
            if (resendError) {
              clearCodeSent(FLOW);
              setError(clerkFailureMessage(resendError));
              return;
            }
            setCode(emptyCode());
            setError(null);
          }}
        />
      </form>
    </AuthScreen>
  );
};

export default SignUpVerifyPage;
